import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import type Graph from 'graphology';
import React, { useEffect, useMemo, useState } from 'react';

import { safeTypeColor } from '../canvas-utils';

type GraphSearchProps = {
  graph: Graph;
  typeFilter: Set<string>;
  onSearchChange: (query: string, matchingNodeIds: Set<string>) => void;
  onNodeSelect: (nodeId: string) => void;
  isLoading: boolean;
};

export const GraphSearch = ({
  graph,
  typeFilter,
  onSearchChange,
  onNodeSelect,
  isLoading,
}: GraphSearchProps) => {
  const [inputValue, setInputValue] = useState('');

  const nodeIndex = useMemo(() => {
    const index: { id: string; label: string; labelLower: string; type: string }[] = [];
    graph.forEachNode((nodeId, attrs) => {
      const label = (attrs.label as string) || '';
      index.push({
        id: nodeId,
        label,
        labelLower: label.toLowerCase(),
        type: attrs.entityType,
      });
    });
    return index.sort((a, b) => a.label.localeCompare(b.label));
  }, [graph]);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return [];
    const queryLower = inputValue.toLowerCase();
    return nodeIndex
      .filter(
        (n) =>
          n.labelLower.includes(queryLower) && (typeFilter.size === 0 || typeFilter.has(n.type))
      )
      .slice(0, 20);
  }, [inputValue, nodeIndex, typeFilter]);

  useEffect(() => {
    if (!inputValue.trim()) {
      onSearchChange('', new Set());
      return;
    }
    const queryLower = inputValue.toLowerCase();
    const matches = new Set<string>();
    nodeIndex.forEach((node) => {
      if (
        node.labelLower.includes(queryLower) &&
        (typeFilter.size === 0 || typeFilter.has(node.type))
      )
        matches.add(node.id);
    });
    onSearchChange(inputValue, matches);
  }, [inputValue, nodeIndex, typeFilter, onSearchChange]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={filteredOptions}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      inputValue={inputValue}
      onInputChange={(_e, v) => setInputValue(v)}
      onChange={(_e, value) => {
        if (value && typeof value !== 'string') onNodeSelect(value.id);
      }}
      filterOptions={(x) => x}
      sx={{ width: '100%' }}
      renderOption={({ key, ...props }, option) => (
        <Box
          component="li"
          key={`${key}-${option.id}-${option.label}`}
          {...props}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box
            sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: safeTypeColor(option.type) }}
          />
          <span>{option.label}</span>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {String(option.type)}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search in nodes..."
          variant="outlined"
          fullWidth
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              height: 40,
              pr: '0 !important',
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 0 !important',
            },
            '& .MuiAutocomplete-endAdornment': {
              right: 8,
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                {inputValue && filteredOptions.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {filteredOptions.length === 20 ? '20+' : filteredOptions.length}
                  </Typography>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
